import React from 'react';
import {Link} from 'react-router-dom';
import {MdAssignment, MdAttachment, MdModeEdit} from 'react-icons/md';
import {DropDown} from '~/components';
import {usePolicySearchRequest} from './context';
import {
    AddressCell,
    CompletedBy,
    DateCell,
    FirmName,
    LockedBy,
    NameCell,
    StatusCell
} from "~/components/Grid/Cells/GridCells";
import {sortDates, sortStrings} from "~/util/sorters";
import {PolicySearchRequestStatus} from "~/pages/PolicySearchRequest/common";
import {isTryonBot} from "~/util/auth";

const headerClassName = 'bg-gray-50 py-4 px-2 border-b text-left text-sm';
const props = {
    className: 'py-4 px-2 text-sm border-b'
};

function sortByLocked(a, b) {
    if (a.statusId == 2 && b.statusId != 2)
        return -1;
    if (a.statusId != 2 && b.statusId == 2)
        return 1;

    return sortStrings(
        `${a.modifiedBy.firstName} ${b.modifiedBy.lastName}`,
        `${b.modifiedBy.firstName} ${b.modifiedBy.lastName}`
    );
}

function sortByCompleter(a, b) {
    if ([3,4].includes(a.statusId) && ![3,4].includes(b.statusId))
        return -1;
    if (![3,4].includes(a.statusId) && [3,4].includes(b.statusId))
        return 1;

    return sortStrings(
        `${a.modifiedBy.firstName} ${b.modifiedBy.lastName}`,
        `${b.modifiedBy.firstName} ${b.modifiedBy.lastName}`
    );
}

function sortByFirm(firms = []) {
    return (a, b) => {
        const aFirm = firms.find(f => f.Id == a.firmId);
        const bFirm = firms.find(f => f.Id == b.firmId);

        return sortStrings(aFirm?.Name, bFirm?.Name);
    };
}

export const PolicySearchGridColumns = firms => [
    {
        title: '',
        key: 'actions',
        canSort: false,
        cell: PolicySearchActionMenu(),
        headerClassName,
        props: {
            className: 'py-4 px-0 border-b'
        }
    },
    {
        title: 'ID',
        field: 'id',
        headerClassName,
        props
    },
    {
        title: 'Submitted',
        cell: DateCell('submitted'),
        sorter: (a,b) => sortDates(a.submitted, b.submitted),
        headerClassName,
        props
    },
    {
        title: 'Address',
        cell: AddressCell,
        sorter: (a,b) => {
            return sortStrings(
                `${a.streetNumber} ${a.streetName} ${a.streetSuffix}`,
                `${b.streetNumber} ${b.streetName} ${b.streetSuffix}`
            );
        },
        headerClassName,
        props
    },
    {
        title: 'County',
        field: 'county',
        headerClassName,
        props
    },
    {
        title: 'Current Owner',
        cell: NameCell,
        sorter: (a,b) => {
            return sortStrings(
                `${a.ownerFirstName} ${a.ownerLastName}`,
                `${b.ownerFirstName} ${b.ownerLastName}`
            );
        },
        headerClassName,
        props
    },
    {
        title: 'Locked By',
        cell: LockedBy,
        sorter: sortByLocked,
        headerClassName,
        props
    },
    {
        title: 'Completed By',
        cell: CompletedBy,
        sorter: sortByCompleter,
        headerClassName,
        props
    },
    {
        title: 'Firm Name',
        cell: FirmName(firms),
        sorter: sortByFirm(firms),
        headerClassName,
        props
    },
    {
        title: 'Status',
        cell: StatusCell,
        sorter: (a,b) => a.statusId - b.statusId,
        width: 196,
        headerClassName,
        props
    }
];

export const PolicySearchActionMenu = () => ({data, ...rest}) => {
    const hasDocs = Array.isArray(data.documents)
        && data.documents.length > 0;
    const {setAttachments, showAttachmentsModal} = usePolicySearchRequest();
    const isModifiedByBot = data.statusId === PolicySearchRequestStatus.Review || isTryonBot(data?.modifiedBy?.roleId);

    return (
        <span {...rest}>
            <DropDown
                wrapperClass="rounded-md border bg-white text-neutral-80 text-sm z-20 filter drop-shadow-xl left-0 top-full mt-1"
                iconClass="text-white"
                iconSize='1.25em'
                buttonClass="p-1 bg-gray-10 text-white focus:outline-none hover:bg-secondary-30 rounded-full flex items-center justify-center"
                text=''>
                <section className="flex flex-col items-stretch w-48 text-gray-80">
                    <p className="font-semibold text-sm p-4 py-2">Action</p>
                    <Link to={`/policySearch/${data.id}?viewOnly=true`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                        <MdAssignment />
                        <span className="ml-2">View</span>
                    </Link>
                    {isModifiedByBot && !data.completed && (
                        <Link to={`/policySearch/${data.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdModeEdit />
                            <span className="ml-2">Update</span>
                        </Link>)}
                    {hasDocs && (
                        <button
                            className='py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center rounded-b-md'
                            onClick={() => {
                                setAttachments(data.documents);
                                showAttachmentsModal();
                            }}>
                            <MdAttachment className='mr-2'/>
                            Attachments
                        </button>
                    )}
                </section>
            </DropDown>
        </span>
    );
};
